import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import JsonLd from "@/components/base/JsonLd";
import { blogArticles } from "@/mocks/blog-articles";

const siteUrl = import.meta.env.VITE_SITE_URL || "https://kakobuypolska.com";

const guideArticles = blogArticles.filter((a) =>
  a.category === "Przewodnik" || a.category === "Tutorial" || a.category === "Poradnik"
);

const guideSections = [
  {
    title: "Dla początkujących",
    icon: "ri-rocket-line",
    desc: "Nigdy nie korzystałeś z Kakobuy? Zacznij tutaj. Te przewodniki przeprowadzą Cię przez cały proces — od rejestracji po pierwsze zamówienie.",
    slugs: ["kakobuy-tutorial", "jak-korzystac-z-kakobuy", "kakobuy-spreadsheet-dla-poczatkujacych", "kakobuy-guide"],
  },
  {
    title: "Bezpieczeństwo zakupów",
    icon: "ri-shield-check-line",
    desc: "Chroń swoje pieniądze i unikaj oszustw. Dowiedz się jak weryfikować sprzedawców i na co zwracać uwagę przy zakupach.",
    slugs: ["czy-kakobuy-jest-bezpieczny", "kakobuy-bezpieczenstwo-zakupow", "jak-sprawdzac-sprzedawcow-kakobuy", "najczestsze-bledy-kakobuy"],
  },
  {
    title: "Wysyłka i logistyka",
    icon: "ri-truck-line",
    desc: "Wszystko o wysyłce z Chin do Polski. Porównaj metody dostawy, poznaj koszty i dowiedz się jak śledzić paczkę.",
    slugs: ["kakobuy-wysylka-polska", "kakobuy-wysylka-do-polski", "kakobuy-tracker", "kakobuy-paczka"],
  },
  {
    title: "Oszczędzanie i porównania",
    icon: "ri-money-dollar-circle-line",
    desc: "Jak kupować taniej i mądrzej. Porównania platform, strategie oszczędzania i najlepsze okazje.",
    slugs: ["jak-znalezc-tanie-produkty-kakobuy", "kakobuy-spreadsheet-tani", "kakobuy-vs-taobao", "kakobuy-vs-weidian"],
  },
];

const guidesJsonLd = {
  "@context": "https://schema.org",
  "@type": "CollectionPage",
  name: "Poradnik zakupowy Kakobuy",
  description: "Kompletny zbiór przewodników, tutoriali i porad dla kupujących na Kakobuy. Od podstaw po zaawansowane strategie.",
  url: `${siteUrl}/guides`,
  mainEntity: {
    "@type": "ItemList",
    itemListElement: guideArticles.map((a, i) => ({
      "@type": "ListItem",
      position: i + 1,
      url: `${siteUrl}/blog/${a.slug}`,
      name: a.title,
    })),
  },
  publisher: {
    "@type": "Organization",
    name: "Kakobuy Spreadsheet Polska",
    logo: {
      "@type": "ImageObject",
      url: "https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/9f7209ca9e1d80750ccbea8c559f256f.png",
    },
  },
};

export default function GuidesPage() {
  return (
    <div className="min-h-[100dvh] flex flex-col">
      <JsonLd data={guidesJsonLd} />
      <Navbar />
      <main className="flex-1 pt-20">
        {/* Hero */}
        <section className="relative min-h-[35dvh] flex items-center overflow-hidden">
          <div className="absolute inset-0 z-0">
            <img
              src="https://readdy.ai/api/search-image?query=Open%20guidebook%20with%20shopping%20bag%20and%20products%20on%20minimal%20wooden%20desk%20warm%20morning%20lighting%20learning%20aesthetic%20editorial%20photography%20clean%20cozy%20atmosphere%20high%20detail&width=1920&height=600&seq=guides-hero&orientation=landscape"
              alt="Poradnik zakupowy Kakobuy"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-foreground-950/70 via-foreground-950/50 to-foreground-950/80" />
          </div>
          <div className="relative z-10 w-full px-4 md:px-8 lg:px-12 py-16 text-center">
            <h1 className="text-3xl md:text-5xl font-heading font-bold text-background-50 mb-3">
              Poradnik zakupowy Kakobuy
            </h1>
            <p className="text-sm md:text-base text-background-200/90 max-w-xl mx-auto">
              Kompletny zbiór przewodników, tutoriali i porad dla kupujących na Kakobuy. Od podstaw po zaawansowane strategie.
            </p>
          </div>
        </section>

        {/* Guide Sections */}
        <section className="w-full px-4 md:px-8 lg:px-12 py-14 md:py-20 bg-background-50">
          <div className="max-w-5xl mx-auto">
            <div className="flex flex-col gap-10 md:gap-14">
              {guideSections.map((section, si) => {
                const articles = section.slugs
                  .map((s) => blogArticles.find((a) => a.slug === s))
                  .filter(Boolean);

                return (
                  <div key={section.title}>
                    <div className="flex items-center gap-3 mb-6">
                      <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-primary-50 text-primary-600">
                        <i className={`${section.icon} text-xl`} />
                      </div>
                      <div>
                        <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950">
                          {section.title}
                        </h2>
                        <p className="text-sm text-foreground-600 mt-1">{section.desc}</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4">
                      {articles.map((article) =>
                        article ? (
                          <Link
                            key={article.slug}
                            to={`/blog/${article.slug}`}
                            className="group flex items-center gap-4 bg-background-100 rounded-xl p-4 md:p-5 border border-background-200/60 hover:border-primary-300/40 hover:shadow-sm transition-all"
                          >
                            <img
                              src={article.image}
                              alt={article.title}
                              className="w-16 h-16 md:w-20 md:h-20 rounded-lg object-cover object-top flex-shrink-0"
                            />
                            <div className="flex-1 min-w-0">
                              <h3 className="text-sm md:text-base font-semibold text-foreground-950 group-hover:text-primary-600 transition-colors line-clamp-2">
                                {article.title}
                              </h3>
                              <div className="flex items-center gap-2 mt-1.5">
                                <span className="text-xs text-foreground-500">{article.readTime}</span>
                                <span className="text-xs font-medium text-primary-600 bg-primary-50 px-2 py-0.5 rounded-full">
                                  {article.category}
                                </span>
                              </div>
                            </div>
                            <i className="ri-arrow-right-line text-foreground-400 group-hover:text-primary-500 transition-colors text-lg flex-shrink-0" />
                          </Link>
                        ) : null
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* All Guides */}
            <div className="mt-14 pt-10 border-t border-background-200">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 text-center mb-8">
                Wszystkie przewodniki
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {guideArticles.map((article) => (
                  <Link
                    key={article.slug}
                    to={`/blog/${article.slug}`}
                    className="group block bg-background-100 rounded-xl p-4 md:p-5 border border-background-200/60 hover:border-primary-300/40 transition-all"
                  >
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-50 text-primary-600 flex-shrink-0">
                        <i className="ri-book-open-line text-lg" />
                      </div>
                      <span className="text-xs font-medium text-primary-600 bg-primary-50 px-2 py-0.5 rounded-full">
                        {article.category}
                      </span>
                    </div>
                    <h3 className="text-sm font-semibold text-foreground-950 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors">
                      {article.title}
                    </h3>
                    <p className="text-xs text-foreground-600 line-clamp-2 leading-relaxed mb-3">
                      {article.excerpt}
                    </p>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-foreground-500">{article.readTime}</span>
                      <span className="text-xs text-primary-500 group-hover:translate-x-1 transition-transform inline-flex items-center gap-1">
                        Czytaj
                        <i className="ri-arrow-right-line" />
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>

            <div className="text-center mt-10">
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="inline-flex items-center gap-2 bg-primary-500 text-background-50 text-sm font-semibold px-6 py-3 rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap"
                >
                  Przejdź do Kakobuy Spreadsheet
                  <i className="ri-external-link-line" />
                </a>
                <Link
                  to="/"
                  className="inline-flex items-center gap-2 text-sm text-foreground-600 bg-background-100 px-6 py-3 rounded-full border border-background-300 hover:bg-background-200 transition-colors whitespace-nowrap"
                >
                  <i className="ri-arrow-left-line" />
                  Strona główna
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}